import { useCallback, useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import { blue } from '@mui/material/colors';
import './style.css'
import { useSelector } from 'react-redux';
import { formatGateway } from '../../util/metamask';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import SearchIcon from '@mui/icons-material/Search';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import LinkIcon from '@mui/icons-material/Link';
import GavelIcon from '@mui/icons-material/Gavel';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import { NavLink } from 'react-router-dom';
import { TOKEN_ID } from '../../router/config';
import { getMetadata as getMetaFromBlock } from '../../util/metamask';

const NFT = ({ hollow, id, menu, noControls, onLoad, owned }) => {

    const labels = useMemo(() => new LocalizedStrings( LOCALIZATIONS ), []),
    [ anchorEl, setAnchorEl ] = useState( null ),
    [ loading, setLoading ] = useState( true ),
    [ imgLoading, setImgLoading ] = useState( true ),
    locale = useSelector( state => state.app.locale ),
    [ metadata, setMetadata ] = useState( null ),
    open = Boolean( anchorEl );

    /**
     * @name closeMenu
     * @function
     * @description Closes the menu
     */
    const closeMenu = () => {
        setAnchorEl( null );
    }

    /**
     * @name preloadImage
     * @function
     * @description Preloads an image (always display skeleton until image is completely loaded)
     * @param {string} url URL of the image to be pre-loaded
     */
    const preloadImage = useCallback( url => {
        setImgLoading( true );
        const img = new Image();
        img.src = url;
        img.onload = () => {
            setImgLoading( false );
        }
    }, [ setImgLoading ]);

    /**
     * @name getImage
     * @function
     * @description 
     * @param {string} uri 
     * @returns {string} IPFS file via a gateway
     */
    const getImage = useCallback( uri => {
        const matches = uri.match( /^ipfs:\/\/(.*?)\/(.*?)$/ ),
        url = formatGateway( matches[ 1 ], matches[ 2 ] );
        preloadImage( url );
        return url;
    }, [ preloadImage ]);

    /**
     * @name getMetadata
     * @function
     * @description Retrieves the metadata
     */
    const getMetadata = useCallback( async () => {

        setLoading( true );
        const data = await getMetaFromBlock( id );

        data.image = getImage( data.image );
        
        setMetadata( data );
        setLoading( false );
        onLoad( data )

    }, [ id, getImage, setMetadata, setLoading, onLoad ]);

    /**
     * @name 
     * @function
     * @description Handles callback for menu items
     * @param {function} callback 
     * @returns 
     */
    const handleClick = callback => () => {
        if( callback ) callback( id, metadata );
        closeMenu();
    }

    /**
     * @name openMenu
     * @function
     * @description Opens the option menu
     * @param {HTMLElement} target 
     */
    const openMenu = ({ target }) => {
        setAnchorEl( target );
    }

    // tell the component to get the metadata
    useEffect(() => {
        if( id && !metadata ) getMetadata();
    }, [ id, metadata, getMetadata ]);

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale )
    }, [ labels, locale ]);

    return (
        <>
            <Card elevation={ 4 } className="coe__nft">
                <CardHeader 
                    action={
                        !loading && !noControls ?
                            <IconButton aria-label="settings" onClick={ openMenu }>
                                <MoreVertIcon />
                            </IconButton> :
                            null
                    }
                    avatar={
                        loading && !hollow ?
                            <CircularProgress /> :
                            <Avatar sx={{ bgcolor: blue[500] }} aria-label="token id">
                                {
                                    hollow ?
                                        <SentimentVeryDissatisfiedIcon /> :
                                        id
                                }
                            </Avatar>
                    }
                    title={ 
                        loading || hollow ?
                            <Skeleton animation={ hollow ? false : "wave" } /> :
                            metadata.name 
                    } />
                    {
                        owned ?
                            <div className="coe__nft__owned">
                                <Typography variant="caption" sx={{ color: '#000000' }}>{ labels.inWallet }</Typography>
                            </div>:
                            null
                    }
                {
                    loading || imgLoading || hollow ?
                        <Skeleton animation={ hollow ? false : "wave" } height={ 200 } variant="rectangular" /> :
                        <CardMedia
                            alt={ metadata.name }
                            height="200"
                            component="img"
                            image={ metadata.image }/>
                }
                <CardContent>
                    <List disablePadding>
                        <ListItem>
                            <ListItemAvatar>
                                <GavelIcon />
                            </ListItemAvatar>
                            <ListItemText 
                                primary={ 'Implicated Party' }
                                secondary={ 
                                    loading || hollow ?
                                        <Skeleton animation={ hollow ? false : "wave" } /> :
                                        metadata.attributes[ 3 ].value
                                } />
                        </ListItem>
                    </List>
                    <Stack justifyContent="space-between" direction="row" sx={{ marginTop: 3 }}>
                        <Box textAlign="center">
                            <Badge badgeContent={  loading ? null : metadata.attributes[ 1 ].value } color="error">
                                <SearchIcon />
                            </Badge>
                            <Typography className="coe__nft__attribute">{ labels.evidence }</Typography>
                        </Box>
                        <Box textAlign="center">
                            <Badge badgeContent={ loading ? null : metadata.attributes[ 0 ].value } color="error">
                                <LinkIcon />
                            </Badge>
                            <Typography className="coe__nft__attribute">{ labels.connections }</Typography>
                        </Box>
                        <Box textAlign="center">
                            <Badge badgeContent={ loading ? null : metadata.attributes[ 2 ].value } color="error">
                                <PeopleAltIcon />
                            </Badge>
                            <Typography className="coe__nft__attribute">{ labels.people }</Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
            <Menu
                anchorEl={ anchorEl }
                onClose={ closeMenu }
                open={ open }>
                    {
                        menu?.map(
                            ({ hide=() => {}, divider, icon, label, to, onClick }, index ) => {

                                const key = 'nft-menu-item-' + index;

                                if( hide( id ) ) return null;

                                if( divider ) return <Divider key={ key } />;

                                return (
                                    <MenuItem component={ to ? NavLink : null } key={ key } onClick={ handleClick( onClick ) } to={ to?.replace( TOKEN_ID, id ) }>
                                        <ListItemIcon>
                                            { icon }
                                        </ListItemIcon>
                                        <ListItemText>
                                            { label }
                                        </ListItemText>
                                    </MenuItem>
                                )
                            }
                        )
                    }
            </Menu>
        </>
    )

}

NFT.defaultProps = {
    hollow: false,
    noControls: false,
    onLoad: () => {}
}

NFT.propTypes = {
    hollow: PropTypes.bool,
    id: PropTypes.number,
    menu: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.object,
            label: PropTypes.string,
            onClick: PropTypes.func,
            to: PropTypes.string
        })
    ),
    noControls: PropTypes.bool,
    owned: PropTypes.bool
}

export default NFT